import { ReactNode, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Collapse, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Toolbar, useMediaQuery, useTheme } from '@mui/material'

import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'

import { NetworkResume } from './NetworkResume'

import { Network } from '@/core/models/network.model'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'
import { useIsWebView } from '@/hooks/useSizeBreakpoints'

type NetworkDrawerProps = {
  menuItems: NetworkMenuItem[]
  network: Network
  /** Controlled open state for the temporary (mobile) drawer. */
  mobileOpen?: boolean
  onMobileClose?: () => void
}

type NetworkMenuItem = {
  id: string
  label: ReactNode
  link?: string
  icon: JSX.Element
  subItems?: NetworkMenuItem[]
}

export default function NetworkDrawer(props: NetworkDrawerProps) {
  const { menuItems, network, mobileOpen = false, onMobileClose } = props
  const theme = useTheme()
  // Below md the permanent drawer would eat the whole screen and there is no way to dismiss it,
  // so switch to a temporary (overlay) drawer toggled from the mobile bar in NetworkPage.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isWebView = useIsWebView()
  const size = isWebView ? 280 : 360

  const content = (
    <>
      <Toolbar sx={{ minHeight: 'unset !important', height: MAIN_HEADER_HEIGHT }} />
      <NetworkResume network={network} />
      <Stack
        flex={1}
        sx={{
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          overscrollBehaviorY: 'contain',
        }}
      >
        <Box mt={0.5} mb={1}>
          <List>
            {menuItems.map((item) => (
              <NestedListItem key={item.id} item={item} pl={2} onNavigate={onMobileClose} />
            ))}
          </List>
        </Box>
      </Stack>
    </>
  )

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ [`& .MuiDrawer-paper`]: { width: 300, boxSizing: 'border-box' } }}
      >
        {content}
      </Drawer>
    )
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: size,
        minHeight: 'inherit',
        height: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)`,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: size,
          boxSizing: 'border-box',
          position: 'fixed',
          left: 60,
        },
      }}
    >
      {content}
    </Drawer>
  )
}

function NestedListItem({ item, pl, onNavigate }: { item: NetworkMenuItem; pl: number; onNavigate?: () => void }) {
  const location = useLocation()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          selected={location.pathname === item.link}
          onClick={(event) => {
            if (item.subItems) {
              event.preventDefault()
              setOpen((prevOpen) => !prevOpen)
            } else if (item.link) {
              navigate(item.link)
              onNavigate?.()
            }
          }}
          sx={{ pl }}
        >
          <ListItemIcon sx={{ minWidth: 30 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
          {item.subItems && (open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />)}
        </ListItemButton>
      </ListItem>
      {item.subItems && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.subItems.map((subItem) => (
              <NestedListItem key={subItem.id} item={subItem} pl={pl + 2} onNavigate={onNavigate} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}
